import type {
  ScoFiscalHoldCardState,
  ScoFiscalHoldCommandKind,
  ScoFiscalHoldOutcome,
  ScoFiscalHoldPhase,
} from '@reform-lite/contracts';

/**
 * EAT-25 — operator words for the held-fiscal-sale codes, shared by the lane
 * panel and the cross-store list. Literal `t('…')` calls on purpose: the
 * catalogue parity spec reads them from source.
 */
export type Translate = (key: string, bakedEnglish?: string) => string;

export function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (text, [name, value]) => text.split(`{${name}}`).join(value),
    template,
  );
}

export function phaseText(t: Translate, phase: ScoFiscalHoldPhase): string {
  switch (phase) {
    case 'commit_unknown':
      return t('terminals.fiscalHold.phase.commitUnknown', 'fiscal outcome unknown');
    case 'fiscalisation_pending':
      return t('terminals.fiscalHold.phase.fiscalisationPending', 'not fiscalised yet');
    case 'identity_pending':
      return t(
        'terminals.fiscalHold.phase.identityPending',
        'fiscalised, receipt number not read back',
      );
    case 'completion_pending':
      return t('terminals.fiscalHold.phase.completionPending', 'fiscalised, not finished');
  }
}

export function cardStateText(t: Translate, cardState: ScoFiscalHoldCardState): string {
  switch (cardState) {
    case 'none':
      return t('terminals.fiscalHold.card.none', 'no card payment');
    case 'authorised':
      return t('terminals.fiscalHold.card.authorised', 'authorised, not captured');
    case 'captured':
      return t('terminals.fiscalHold.card.captured', 'captured: the customer was charged');
    case 'unknown':
      return t('terminals.fiscalHold.card.unknown', 'unknown: the customer may have been charged');
  }
}

/** A card leg a void would reverse, or might: the operator is warned first. */
export function cardAtRisk(cardState: ScoFiscalHoldCardState): boolean {
  return cardState === 'captured' || cardState === 'unknown';
}

export function kindText(t: Translate, kind: ScoFiscalHoldCommandKind): string {
  return kind === 'retry'
    ? t('terminals.fiscalHold.kind.retry', 'Retry')
    : t('terminals.fiscalHold.kind.void', 'Void');
}

export function outcomeText(t: Translate, outcome: ScoFiscalHoldOutcome): string {
  switch (outcome) {
    case 'applied_completed':
      return t('terminals.fiscalHold.outcome.appliedCompleted', 'fiscalised and finished');
    case 'retry_failed':
      return t('terminals.fiscalHold.outcome.retryFailed', 'retried, failed again');
    case 'applied_voided':
      return t('terminals.fiscalHold.outcome.appliedVoided', 'voided');
    case 'applied_voided_card_reversed':
      return t(
        'terminals.fiscalHold.outcome.appliedVoidedCardReversed',
        'voided, card payment reversed',
      );
    case 'card_release_failed':
      return t(
        'terminals.fiscalHold.outcome.cardReleaseFailed',
        'voided, but the card authorisation could not be released',
      );
    case 'card_reversal_failed':
      return t(
        'terminals.fiscalHold.outcome.cardReversalFailed',
        'voided, but the card payment could not be reversed',
      );
    case 'not_found':
      return t('terminals.fiscalHold.outcome.notFound', 'the lane no longer holds this sale');
    case 'phase_changed':
      return t('terminals.fiscalHold.outcome.phaseChanged', 'not applied: the sale had moved on');
    case 'not_voidable':
      return t('terminals.fiscalHold.outcome.notVoidable', 'not applied: not voidable');
    case 'busy':
      return t('terminals.fiscalHold.outcome.busy', 'not applied: the lane was busy');
    case 'device_unavailable':
      return t(
        'terminals.fiscalHold.outcome.deviceUnavailable',
        'not applied: the fiscal device or card terminal is down',
      );
    case 'expired':
      return t('terminals.fiscalHold.outcome.expired', 'expired before the lane applied it');
  }
}

/** How old a report is, in the largest sensible unit. */
export function ageText(t: Translate, ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return fill(t('terminals.fiscalHold.age.seconds', '{n} s'), { n: String(seconds) });
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return fill(t('terminals.fiscalHold.age.minutes', '{n} min'), { n: String(minutes) });
  }
  return fill(t('terminals.fiscalHold.age.hours', '{h} h {m} min'), {
    h: String(Math.floor(minutes / 60)),
    m: String(minutes % 60),
  });
}

export function when(iso: string | null): string {
  if (iso === null) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  );
}
